import { Link, useSearchParams } from "react-router";
import { useMemo, useState, type ReactNode } from "react";
import {
  useGetIdentity,
  useGetList,
  useListContext,
  useRedirect,
  type InputProps,
} from "ra-core";
import { DataTable } from "@/components/admin/data-table";
import { DateField } from "@/components/admin/date-field";
import { ExportButton } from "@/components/admin/export-button";
import { FilterButton } from "@/components/admin/filter-form";
import { List } from "@/components/admin/list";
import { SearchInput } from "@/components/admin/search-input";
import { SelectInput } from "@/components/admin/select-input";
import { TextInput } from "@/components/admin/text-input";
import { BooleanInput } from "@/components/admin/boolean-input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Clapperboard,
  ExternalLink,
  Film,
  LayoutDashboard,
  List as ListIcon,
  MessageSquareText,
  Star,
} from "lucide-react";

import { TopToolbar } from "../layout/TopToolbar";
import type { Contact, EchoflicksVideoSession } from "../types";
import { EmptyHint, Rating } from "./EchoflicksSessionList";
import { TestEchoBadge } from "./TestEchoBadge";
import { WebEchoBadge } from "./WebEchoBadge";
import { echoLanguageFilter } from "./echoLanguageFilter";
import { isStudioFilm } from "./studioFilms";
import {
  StudioAgencyCell,
  StudioCreatorCell,
  StudioFilmAgencyLine,
  StudioFilmUserCell,
} from "./StudioFilmCells";
import { StudioDirectoryContext } from "./studioDirectoryContext";
import { studioAgencyChoices, studioFilmFilters } from "./studioFilmFilters";
import { useStudioDirectory } from "./useStudioDirectory";
import {
  formatDateTime,
  sessionStatusLabel,
  statusBadgeVariant,
} from "./sessionUtils";
import { sessionFinalVideoSource, useMediaUrl } from "./useMediaUrl";
import { EchoSaveButton } from "./EchoSaveButton";
import {
  isImageOptimizerAvailable,
  optimizedImageUrl,
  optimizedSrcSet,
  sessionPosterSource,
  type OptimizedImageWidth,
} from "./posterImage";

type EchoColumn = {
  id: string;
  label: string;
  description: string;
  matches: (echo: EchoflicksVideoSession) => boolean;
};

type EchoView = "board" | "list";

const echoColumns: EchoColumn[] = [
  {
    id: "loved",
    label: "Loved",
    description: "5 star echoes",
    matches: (echo) => echo.rating === 5,
  },
  {
    id: "positive",
    label: "Positive",
    description: "4 star echoes",
    matches: (echo) => echo.rating === 4,
  },
  {
    id: "needs_attention",
    label: "Needs Attention",
    description: "1-3 star echoes",
    matches: (echo) => Boolean(echo.rating && echo.rating <= 3),
  },
  {
    id: "unrated",
    label: "Unrated",
    description: "delivered, awaiting signal",
    matches: (echo) => !echo.rating,
  },
];

const ratingChoices = [
  { id: 5, name: "5 stars" },
  { id: 4, name: "4 stars" },
  { id: 3, name: "3 stars" },
  { id: 2, name: "2 stars" },
  { id: 1, name: "1 star" },
];

const WrapperField = ({ children }: InputProps & { children: ReactNode }) =>
  children;

const echoFilters = [
  <SearchInput source="q" alwaysOn />,
  <WrapperField source="rating" label="Rating">
    <SelectInput
      source="rating"
      label={false}
      emptyText="Any rating"
      choices={ratingChoices}
      optionText="name"
      optionValue="id"
    />
  </WrapperField>,
  <WrapperField source="echo_origin" label="Test echoes">
    <SelectInput
      source="echo_origin"
      label={false}
      // Default empty: a test Echo stays visible until an operator opts into
      // hiding it. "organic" is the dashboard-API's "no echo_origin" sentinel.
      emptyText="Show test echoes"
      choices={[
        { id: "organic", name: "Hide test echoes" },
        { id: "test_autoqueue", name: "Only test echoes" },
      ]}
      optionText="name"
      optionValue="id"
    />
  </WrapperField>,
  echoLanguageFilter(),
  <TextInput source="poc_collection" label="POC collection" />,
  // Adding this filter switches it on at once; the data providers resolve the
  // toggle into the signed-in user's saved echo ids (see savedEchoes.ts).
  <BooleanInput source="saved" label="Saved echoes" defaultValue />,
];

export const EchoList = () => {
  const { identity } = useGetIdentity();
  const [searchParams, setSearchParams] = useSearchParams();
  const directory = useStudioDirectory();
  // B6.3: agency, creator, made by staff, format (stable across renders)
  const filters = useMemo(
    () => [
      ...echoFilters,
      ...studioFilmFilters(studioAgencyChoices(directory)),
    ],
    [directory],
  );
  const view: EchoView =
    searchParams.get("view") === "board" ? "board" : "list";

  if (!identity) return null;

  const handleViewChange = (nextView: EchoView) => {
    const nextParams = new URLSearchParams(searchParams);
    if (nextView === "list") {
      nextParams.delete("view");
    } else {
      nextParams.set("view", nextView);
    }
    setSearchParams(nextParams);
  };

  return (
    <StudioDirectoryContext.Provider value={directory}>
      <List
        title={false}
        perPage={view === "list" ? 25 : 100}
        sort={{ field: "display_date", order: "DESC" }}
        filters={filters}
        actions={<EchoActions view={view} onViewChange={handleViewChange} />}
        pagination={view === "board" ? null : undefined}
      >
        {view === "list" ? <EchoTable /> : <EchoBoard />}
      </List>
    </StudioDirectoryContext.Provider>
  );
};

const EchoActions = ({
  view,
  onViewChange,
}: {
  view: EchoView;
  onViewChange: (view: EchoView) => void;
}) => (
  <TopToolbar className="flex-wrap">
    <div className="inline-flex rounded-md border bg-background p-0.5">
      <Button
        type="button"
        size="sm"
        variant={view === "board" ? "secondary" : "ghost"}
        aria-pressed={view === "board"}
        onClick={() => onViewChange("board")}
      >
        <LayoutDashboard className="size-4" />
        Board
      </Button>
      <Button
        type="button"
        size="sm"
        variant={view === "list" ? "secondary" : "ghost"}
        aria-pressed={view === "list"}
        onClick={() => onViewChange("list")}
      >
        <ListIcon className="size-4" />
        List
      </Button>
    </div>
    <FilterButton />
    <ExportButton />
  </TopToolbar>
);

const EchoBoard = () => {
  const {
    data: echoes = [],
    isPending,
    filterValues,
  } = useListContext<EchoflicksVideoSession>();
  const hasFilters = filterValues && Object.keys(filterValues).length > 0;

  if (isPending) return null;

  if (echoes.length === 0) {
    return (
      <EmptyHint>
        {hasFilters
          ? "No echoes match these filters."
          : "No echoes found. Run the local importer to load completed generated videos."}
      </EmptyHint>
    );
  }

  return (
    <div className="w-full overflow-x-auto pb-4">
      <div className="flex min-w-[1080px] gap-4">
        {echoColumns.map((column) => {
          const columnEchoes = echoes.filter(column.matches);
          return (
            <EchoColumn key={column.id} column={column} echoes={columnEchoes} />
          );
        })}
      </div>
    </div>
  );
};

const EchoTable = () => (
  <DataTable<EchoflicksVideoSession> rowClick="show" bulkActionButtons={false}>
    <DataTable.Col
      source="movie_title"
      label="Echo"
      render={(record) => (
        <EchoTitleCell record={record as EchoflicksVideoSession} />
      )}
    />
    <DataTable.Col
      source="contact_name"
      label="User"
      render={(record) => (
        <EchoUserCell record={record as EchoflicksVideoSession} />
      )}
    />
    <DataTable.Col
      source="studio_agency_id"
      label="Agency"
      disableSort
      render={(record) => (
        <StudioAgencyCell record={record as EchoflicksVideoSession} />
      )}
    />
    <DataTable.Col
      source="studio_created_by"
      label="Creator"
      disableSort
      render={(record) => (
        <StudioCreatorCell record={record as EchoflicksVideoSession} />
      )}
    />
    <DataTable.Col
      source="rating"
      label="Rating"
      sortByOrder="DESC"
      render={(record) => <Rating rating={record.rating} />}
    />
    <DataTable.Col
      source="status"
      label="Status"
      render={(record) => (
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge variant={statusBadgeVariant(record.status)}>
            {sessionStatusLabel(record.status)}
          </Badge>
          <WebEchoBadge record={record as EchoflicksVideoSession} />
          <TestEchoBadge record={record as EchoflicksVideoSession} />
        </div>
      )}
    />
    <DataTable.Col source="display_date" label="Date">
      <DateField source="display_date" showTime />
    </DataTable.Col>
    <DataTable.Col
      label=""
      disableSort
      className="w-12 text-right"
      render={(record) => <EchoSaveButton echoId={String(record.id)} />}
    />
  </DataTable>
);

const EchoTitleCell = ({ record }: { record: EchoflicksVideoSession }) => {
  const title = record.movie_title ?? record.script_title ?? "Untitled echo";
  const showPath = `/echoflicks_echoes/${record.id}/show`;
  return (
    <div className="flex items-center gap-3">
      <Link
        to={showPath}
        aria-label={`Open ${title}`}
        className="block shrink-0 overflow-hidden rounded-md bg-muted"
        onClick={(event) => event.stopPropagation()}
      >
        <EchoThumbnail echo={record} />
      </Link>
      <div className="min-w-0">
        <Link
          to={showPath}
          className="block truncate font-medium hover:underline"
          onClick={(event) => event.stopPropagation()}
        >
          {title}
        </Link>
        <div className="truncate text-xs text-muted-foreground">
          {record.session_id ?? record.source_id}
        </div>
      </div>
    </div>
  );
};

const EchoUserCell = ({ record }: { record: EchoflicksVideoSession }) => {
  if (isStudioFilm(record)) return <StudioFilmUserCell record={record} />;
  const label = record.contact_name || record.whatsapp_user_id;
  const content = (
    <div className="min-w-0">
      <div className="truncate font-medium">{label}</div>
      <div className="text-xs text-muted-foreground">
        {record.whatsapp_user_id}
      </div>
    </div>
  );

  if (!record.contact_id) return content;

  return (
    <Link
      to={`/contacts/${record.contact_id}/show`}
      className="block no-underline hover:underline"
      onClick={(event) => event.stopPropagation()}
    >
      {content}
    </Link>
  );
};

const EchoColumn = ({
  column,
  echoes,
}: {
  column: EchoColumn;
  echoes: EchoflicksVideoSession[];
}) => (
  <section className="w-1/4 min-w-[250px] flex-1 pb-8">
    <div className="flex flex-col items-center">
      <h3 className="text-base font-medium">{column.label}</h3>
      <p className="text-sm text-muted-foreground">
        {echoes.length} {echoes.length === 1 ? "echo" : "echoes"} /{" "}
        {column.description}
      </p>
    </div>
    <div className="mt-2 flex flex-col gap-2 rounded-md">
      {echoes.map((echo) => (
        <EchoCard key={echo.id} echo={echo} />
      ))}
      {echoes.length === 0 ? (
        <div className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">
          No echoes here.
        </div>
      ) : null}
    </div>
  </section>
);

export const EchoCard = ({
  echo,
  compact,
}: {
  echo: EchoflicksVideoSession;
  compact?: boolean;
}) => {
  const redirect = useRedirect();
  const studioFilm = isStudioFilm(echo);
  const title = echo.movie_title ?? echo.script_title ?? "Untitled echo";
  const handleClick = () => {
    redirect(
      `/echoflicks_echoes/${echo.id}/show`,
      undefined,
      undefined,
      undefined,
      {
        _scrollToTop: true,
      },
    );
  };

  return (
    <Card
      className="cursor-pointer py-0 shadow-sm transition-shadow hover:shadow-md"
      onClick={handleClick}
    >
      <CardContent className="p-0">
        <EchoPreview echo={echo} compact={compact} />
        <div className="space-y-3 p-3">
          <div className="space-y-1">
            <div className="line-clamp-2 text-sm font-medium">{title}</div>
            <div className="text-xs text-muted-foreground">
              {studioFilm ? (
                <StudioFilmAgencyLine record={echo} />
              ) : echo.contact_id ? (
                <Link
                  to={`/contacts/${echo.contact_id}/show`}
                  className="hover:underline"
                  onClick={(event) => event.stopPropagation()}
                >
                  {echo.contact_name || echo.whatsapp_user_id}
                </Link>
              ) : (
                echo.contact_name || echo.whatsapp_user_id
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-1.5">
            <Badge variant={statusBadgeVariant(echo.status)}>
              {sessionStatusLabel(echo.status)}
            </Badge>
            {echo.rating ? (
              <Badge variant="secondary">
                <Star className="size-3 fill-current" />
                {echo.rating}/5
              </Badge>
            ) : null}
            <WebEchoBadge record={echo} />
            <TestEchoBadge record={echo} />
          </div>

          <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
            <div>
              <div className="font-medium text-foreground">
                {echo.script_scene_count ?? "?"}
              </div>
              scenes
            </div>
            <div>
              <div className="font-medium text-foreground">
                {echo.uploaded_media_count ?? 0}
              </div>
              uploads
            </div>
          </div>

          <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
            <span>
              {formatDateTime(echo.completed_at ?? echo.display_date)}
            </span>
            {echo.feedback_text ? (
              <MessageSquareText className="size-4" />
            ) : null}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

/**
 * The echo's generated poster, resized by Vercel's image optimizer to the box
 * it is drawn in. Degrades in steps: optimized → the raw signed JPEG (if the
 * optimizer is unavailable) → the caller's fallback. Renders nothing until the
 * signed URL is known so the fallback never flashes behind a loading image.
 */
const PosterImage = ({
  echo,
  widths,
  sizes,
  className,
  fallback,
}: {
  echo: EchoflicksVideoSession;
  widths: readonly OptimizedImageWidth[];
  /** The rendered width the browser should pick a `srcset` candidate for. */
  sizes: string;
  className: string;
  fallback: ReactNode;
}) => {
  const poster = useMediaUrl(sessionPosterSource(echo));
  const [optimize, setOptimize] = useState(isImageOptimizerAvailable);
  const [failed, setFailed] = useState(false);

  if (!poster.url || failed) return fallback;
  return (
    <img
      src={optimizedImageUrl(poster.url, widths[0], { enabled: optimize })}
      srcSet={
        optimize
          ? optimizedSrcSet(poster.url, widths, { enabled: true })
          : undefined
      }
      sizes={optimize ? sizes : undefined}
      alt=""
      loading="lazy"
      decoding="async"
      draggable={false}
      onError={() => (optimize ? setOptimize(false) : setFailed(true))}
      className={className}
    />
  );
};

/** List-row thumbnail: the poster at 4:3, or a film icon for echoes without one. */
const EchoThumbnail = ({ echo }: { echo: EchoflicksVideoSession }) => (
  <div className="flex h-16 w-[5.333rem] items-center justify-center bg-muted text-muted-foreground">
    <PosterImage
      echo={echo}
      widths={[96, 192]}
      sizes="86px"
      className="size-full object-cover"
      fallback={<Film className="size-5" aria-hidden="true" />}
    />
  </div>
);

/** Board-card preview: the poster when the echo has one, else the video's own frame. */
const EchoPreview = ({
  echo,
  compact,
}: {
  echo: EchoflicksVideoSession;
  compact?: boolean;
}) => {
  const aspect = compact ? "aspect-[4/3]" : "aspect-video";
  return (
    <PosterImage
      echo={echo}
      widths={[384, 768]}
      sizes="(min-width: 1080px) 280px, 50vw"
      className={`w-full rounded-t-md bg-black object-cover ${aspect}`}
      fallback={<VideoPreview echo={echo} aspect={aspect} />}
    />
  );
};

const VideoPreview = ({
  echo,
  aspect,
}: {
  echo: EchoflicksVideoSession;
  aspect: string;
}) => {
  const finalVideo = useMediaUrl(sessionFinalVideoSource(echo));

  if (finalVideo.url) {
    return (
      <video
        src={finalVideo.url}
        preload="metadata"
        muted
        onError={finalVideo.refresh}
        className={`w-full rounded-t-md bg-black object-cover ${aspect}`}
      />
    );
  }

  return (
    <div
      className={`flex w-full items-center justify-center rounded-t-md bg-muted text-muted-foreground ${aspect}`}
    >
      {finalVideo.isLoading ? (
        <span className="text-xs">Signing media...</span>
      ) : (
        <Film className="size-6" />
      )}
    </div>
  );
};

const CONTACT_ECHOES_PAGE = 12;

export const ContactEchoesPanel = ({ contact }: { contact: Contact }) => {
  const { data: echoes = [], isPending } = useGetList<EchoflicksVideoSession>(
    "echoflicks_echoes",
    {
      filter: { contact_id: contact.id },
      pagination: { page: 1, perPage: CONTACT_ECHOES_PAGE },
      sort: { field: "display_date", order: "DESC" },
    },
  );

  if (isPending) return null;

  if (echoes.length === 0) {
    return (
      <EmptyHint>
        No finished echoes are linked to this contact yet. Completed generated
        videos will appear here.
      </EmptyHint>
    );
  }

  // The tiles read only the newest page (G25): say so once it is full. The
  // Customer profile card above has the all-time numbers.
  const scope = echoes.length >= CONTACT_ECHOES_PAGE ? " (last 12)" : "";

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <EchoMetric label={`Echoes${scope}`} value={echoes.length.toString()} />
        <EchoMetric
          label={`Rated${scope}`}
          value={echoes.filter((echo) => echo.rating).length.toString()}
        />
        <EchoMetric
          label={`Avg rating${scope}`}
          value={averageRatingLabel(echoes)}
        />
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {echoes.slice(0, 6).map((echo) => (
          <EchoCard key={echo.id} echo={echo} compact />
        ))}
      </div>
      <Button asChild variant="outline" size="sm">
        <Link
          to={`/echoflicks_echoes?filter=${encodeURIComponent(
            JSON.stringify({ contact_id: contact.id }),
          )}`}
        >
          <Clapperboard className="size-4" />
          Explore all echoes
          <ExternalLink className="size-4" />
        </Link>
      </Button>
    </div>
  );
};

const EchoMetric = ({ label, value }: { label: string; value: string }) => (
  <div className="rounded-md border p-3">
    <div className="text-sm text-muted-foreground">{label}</div>
    <div className="mt-1 text-xl font-semibold">{value}</div>
  </div>
);

function averageRatingLabel(echoes: EchoflicksVideoSession[]) {
  const rated = echoes.filter((echo) => echo.rating);
  if (rated.length === 0) return "No rating";
  const average =
    rated.reduce((sum, echo) => sum + (echo.rating ?? 0), 0) / rated.length;
  return `${average.toFixed(1)}/5`;
}
